import { useEffect, useState } from "react";
import { ChatCircleDots, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";

export default function SmsEventPreferences({ masterOn }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/me/sms-preferences");
      setEvents(r.data.events || []);
    } catch {
      setEvents([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key_name, next) => {
    setEvents(events.map(e => e.key_name === key_name ? { ...e, enabled: next ? 1 : 0 } : e));
    try {
      await api.put("/users/me/sms-preferences", { event_key: key_name, enabled: next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      setEvents(events.map(e => e.key_name === key_name ? { ...e, enabled: next ? 0 : 1 } : e));
    }
  };

  if (loading) return null;

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-6" data-testid="sms-event-preferences">
      <div className="flex items-center gap-2 pb-3 border-b border-[#E5E5E5] mb-4">
        <ChatCircleDots size={18} className="text-[#0055FF]" />
        <h2 className="text-lg font-bold text-[#0A0A0A]">SMS Event Preferences</h2>
      </div>
      {!masterOn && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 mb-4 text-xs text-[#D97706]">
          Enable "SMS Notifications" and add a phone number above to activate these.
        </div>
      )}
      {events.length === 0 ? (
        <p className="text-xs text-[#A3A3A3]">
          None of your role groups are subscribed to any SMS events. A platform admin controls which
          roles receive which events from <strong>System Admin → SMS Console → Routing Rules</strong>.
        </p>
      ) : (
        <>
          <p className="text-xs text-[#525252] mb-3">
            These are the events your role groups are subscribed to. Untick any you don't want to be texted about — the opt-out only affects you.
          </p>
          <div className="divide-y divide-[#F5F5F5] border border-[#E5E5E5]">
            {events.map(ev => (
              <label key={ev.id} className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors ${!masterOn ? "opacity-60" : ""}`} data-testid={`sms-pref-${ev.key_name}`}>
                <input
                  type="checkbox"
                  checked={!!ev.enabled}
                  onChange={e => toggle(ev.key_name, e.target.checked)}
                  disabled={!masterOn}
                  className="mt-0.5 h-4 w-4 rounded-none border-[#D4D4D4] text-[#0055FF] focus:ring-[#0055FF]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#0A0A0A]">{ev.label}</span>
                    {ev.enabled ? <CheckCircle size={12} weight="fill" className="text-[#16A34A]" /> : null}
                  </div>
                  <p className="text-xs text-[#A3A3A3] mt-0.5">{ev.description}</p>
                </div>
              </label>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
